/**
 * Settings → Compliance — the page's words. Story 26.6.
 *
 * Kept apart by WHERE EACH WORD COMES FROM, as `connect-copy.ts` and `publish-copy.ts`
 * are, because each kind is held to a different source by `tests/compliance-copy.test.ts`:
 *
 *   DRAWN     `design/screens/settings-compliance.html` draws it, in one of its six
 *             states. Held to the mockup, verbatim.
 *   SHAPED    drawn with a name, a number or a date in it — "Accept version 2",
 *             "Version 1". The function is held to the drawing by calling it with the
 *             drawing's own values.
 *   COMPOSED  no capture draws it: the toasts (the manifest DESCRIBES them — "the verb
 *             matching its button" — and draws none), a save that fails on a card whose
 *             failure is not drawn, and a page that cannot load. Built on the drawn
 *             sentences' shapes and listed here so a round-trip replaces them in one place.
 *
 * NO RUNTIME IMPORTS, so a node-side test can load this file without resolving the
 * workspace.
 */
export const DRAWN = {
  title: 'Compliance',
  lead: 'What respondents are told, what we record about them, and the agreement that covers it.',

  disclosureTitle: 'What respondents are told about AI',
  disclosureHelp: 'Shown on every funnel that uses AI to read answers, before anything is sent.',
  /**
   * The fixed minimum, as the runtime prints it. The runtime's own constant is
   * `AI_DISCLOSURE` in `packages/embed/src/disclosure.ts`; `tests/disclosure-copy.test.ts`
   * holds the two equal, because this row promises a respondent sees exactly this.
   */
  minimum: 'This form uses AI to read your answers.',
  alwaysShown: 'Always shown',
  ownLabel: 'Add a sentence of your own',
  optional: '(optional)',
  ownHelper: 'Shown after the sentence above. Keep it to one or two sentences.',
  previewLabel: 'How it reads on your funnel',
  previewButton: 'Continue',
  saveDisclosure: 'Save disclosure',
  /** The `error` state: in the helper's place, under the textarea. */
  saveFailed: "We couldn't save that. Check your connection and try again — your text is still here.",

  consentTitle: 'Consent wording',
  consentHelp: "New consent blocks start with this text. Funnels you've already built keep their own until you change them.",
  saveConsent: 'Save consent wording',

  ipTitle: 'IP addresses',
  ipHelp: 'An IP address is personal data. Choose how much of it is kept with each lead.',
  ipGroup: 'IP address capture',
  ipDefault: 'Default',
  ipNote: 'A change applies from the next submission. Leads you already have keep what was recorded.',
  saveIp: 'Save IP setting',

  dpaTitle: 'Data Processing Agreement',
  dpaHelp: "Leadgible processes your leads on your behalf. The DPA says how, and it's the same for every customer.",
  dpaNone: 'Not accepted yet.',
  dpaAccept: 'Accept the DPA',
  dpaOwnerOnly: 'Only an Owner can accept the DPA.',
  readDpa: 'Read the DPA',
  subProcessors: 'Sub-processors',
} as const

/** PRD §3.15's three modes, in the drawing's order — the default first — with the drawing's sentence for each. */
export const IP_MODES = [
  {
    mode: 'anonymised',
    label: 'Anonymised',
    detail:
      'The last part of each address is zeroed, so it points to a neighbourhood rather than a device. Country and city still work.',
  },
  {
    mode: 'full',
    label: 'Full',
    detail: 'The whole address is kept with the lead. Only choose this if you need it and can say why.',
  },
  {
    mode: 'off',
    label: 'Off',
    detail: 'No address is kept. Country and city still come from the connection.',
  },
] as const

export const SHAPED = {
  /** "I accept the Data Processing Agreement on behalf of Northside Studio." */
  acceptOnBehalf: (organisation: string) => `I accept the Data Processing Agreement on behalf of ${organisation}.`,
  /** "Version 1" */
  version: (version: number) => `Version ${version}`,
  /** "Accept version 2" */
  acceptVersion: (version: number) => `Accept version ${version}`,
  /**
   * "Version 2 was published on 1 November 2026. Read it and accept it to keep your record
   * current — version 1 stays on file."
   */
  newVersion: (version: number, publishedOn: string, onFile: number) =>
    `Version ${version} was published on ${publishedOn}. Read it and accept it to keep your record current — version ${onFile} stays on file.`,
  /** "Accepted by **Ariel Kessler**, Owner, on 23 September 2026 at 09:14" — the words around the name. */
  acceptedBy: 'Accepted by',
  acceptedOn: (date: string, time: string) => `, Owner, on ${date} at ${time}`,
} as const

export const COMPOSED = {
  /** The toasts: "the verb matching its button" (the manifest), as Profile's are. */
  disclosureSaved: 'Disclosure saved',
  consentSaved: 'Consent wording saved',
  ipSaved: 'IP setting saved',
  dpaAccepted: 'DPA accepted',
  /** A failed save where no text is at stake: the drawn sentence, without its last clause. */
  saveFailedPlain: "We couldn't save that. Check your connection and try again.",
  /** The page cannot be read at all — not a drawn state. The API's own message is shown when it sent one. */
  loadFailed: "We couldn't load your compliance settings. Check your connection and try again.",
  retry: 'Try again',
  /** An Admin, where nobody has accepted yet: the two drawn sentences, together. */
} as const
